import { IMAGE_SIZES } from "@/constants/ui"
import { DetailedInspiration } from "@/types/inspiration"
import { BusinessHour, DetailedPlace, Place } from "@/types/place"
import {
  CurrentUser,
  CurrentUserProfile,
  PublicUser,
  PublicUserProfile,
} from "@/types/user"

import cloudflareLoader from "@/lib/cloudflare-loader"
import { getFirstTwoSentences } from "@/lib/entity-utils"
import {
  formatRestaurantPrice,
  formatSnakeCase,
  formatTimeOfDayString,
  formatYearMonthDayHourMinSecTz,
  starRatingToNumber,
} from "@/lib/format-utils"
import { isAnsweredQuestion } from "@/lib/place-utils"
import { compactBlank } from "@/lib/utils/collections"
import { BASE_CANONICAL_URL } from "@/lib/utils/constants"
import { formatPhone } from "./phone-utils"
import { isEventInPast, isEventSoldOut } from "./place-utils"

interface PersonSchemaOptions {
  likesCount?: number
  postsCount?: number
  isSearchableUser?: boolean
}

export function generatePersonSchema(
  user:
    | CurrentUser
    | PublicUser
    | { first_name?: string; handle?: string; image_url?: string },
  userProfile?: CurrentUserProfile | PublicUserProfile,
  options: PersonSchemaOptions = {}
) {
  const handle = "handle" in user && user.handle ? user.handle : undefined
  let name: string | undefined
  if (
    options.isSearchableUser &&
    "first_name" in user &&
    "last_name" in user &&
    (user.first_name || (user as CurrentUser | PublicUser).last_name)
  ) {
    name = [user.first_name, (user as CurrentUser | PublicUser).last_name]
      .filter(Boolean)
      .join(" ")
  } else if (handle) {
    name = handle
  } else if ("first_name" in user && user.first_name) {
    name = user.first_name
  }

  const alternateName = handle ? `@${handle}` : undefined
  const image = "image_url" in user ? user.image_url : undefined
  const description = userProfile?.bio as string | undefined
  const followersCount =
    userProfile && "followers_count" in userProfile
      ? (userProfile as CurrentUserProfile).followers_count
      : undefined

  const sameAs: string[] = []
  if (userProfile?.instagram) sameAs.push(userProfile.instagram)
  if (userProfile?.tiktok) sameAs.push(userProfile.tiktok)
  if (userProfile?.youtube) sameAs.push(userProfile.youtube)

  // Interaction statistics
  const interactionStatistic = []
  if (typeof followersCount === "number") {
    interactionStatistic.push({
      "@type": "InteractionCounter",
      interactionType: "https://schema.org/FollowAction",
      userInteractionCount: followersCount,
    })
  }
  if (typeof options.likesCount === "number") {
    interactionStatistic.push({
      "@type": "InteractionCounter",
      interactionType: "https://schema.org/LikeAction",
      userInteractionCount: options.likesCount,
    })
  }
  const agentInteractionStatistic =
    typeof options.postsCount === "number"
      ? {
          "@type": "InteractionCounter",
          interactionType: "https://schema.org/WriteAction",
          userInteractionCount: options.postsCount,
        }
      : undefined

  return compactBlank({
    "@type": "Person",
    name,
    alternateName,
    description,
    image,
    sameAs: sameAs.length > 0 ? sameAs : undefined,
    interactionStatistic:
      interactionStatistic.length > 0 ? interactionStatistic : undefined,
    agentInteractionStatistic,
  })
}

function buildPlaceSchema<T extends DetailedPlace>(
  schemaType: string,
  place: T,
  extraFields: Record<string, unknown> = {}
) {
  return compactBlank({
    "@context": "https://schema.org",
    "@type": schemaType,
    name: place.name,
    description: place.description,
    image: place.photos?.[0]?.url
      ? cloudflareLoader({
          src: place.photos[0].url,
          width: IMAGE_SIZES.xl,
        })
      : undefined,
    url: place.canonical_url,
    telephone:
      place.phone && place.phone.trim() !== ""
        ? formatPhone(place.phone, "US")
        : undefined,
    address: compactBlank({
      "@type": "PostalAddress",
      // omit streetAddress as we don't reliably have it
      addressLocality: place.city,
      addressRegion: place.region,
      postalCode: place.zip_code,
      addressCountry: place.country_code,
    }),
    geo: compactBlank({
      "@type": "GeoCoordinates",
      latitude: place.latitude,
      longitude: place.longitude,
    }),
    openingHoursSpecification: Array.isArray(place.business_hours)
      ? place.business_hours.map((bh: BusinessHour) => ({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: Array.isArray(bh.day) ? bh.day : [bh.day],
          opens: bh.times?.[0]?.split("-")[0]?.trim() || undefined,
          closes: bh.times?.[0]?.split("-")[1]?.trim() || undefined,
        }))
      : undefined,
    ...extraFields,
  })
}

export function generatePlaceSchema(place: Place) {
  if (!("detailed" in place) || !place.detailed) return null

  switch (place.type) {
    case "restaurant":
      return buildPlaceSchema("FoodEstablishment", place, {
        priceRange: formatRestaurantPrice(place, "$"),
        servesCuisine: (place.cuisines ?? []).map(formatSnakeCase),
      })
    case "hotel":
      return buildPlaceSchema("Hotel", place, {
        checkinTime: formatTimeOfDayString(place.check_in),
        checkoutTime: formatTimeOfDayString(place.check_out),
        starRating:
          starRatingToNumber(place.star_rating) != null
            ? {
                "@type": "Rating",
                ratingValue: starRatingToNumber(place.star_rating),
                bestRating: 5,
              }
            : undefined,
      })
    case "attraction":
      return buildPlaceSchema("TouristAttraction", place)
    case "event": {
      const schedule = place.schedules?.data?.[0]
      const hasSchedule = schedule && schedule.start_at
      const eventInPast = isEventInPast(place)

      // map actor types to appropriate schema.org types
      const performers = place.event_actors?.map((actor) => {
        let schemaType = "Person"
        if (actor.actor_type === "team") {
          schemaType = "SportsTeam"
        } else if (
          actor.actor_type === "band" ||
          actor.actor_type === "music_group" ||
          actor.actor_type === "tribute_band"
        ) {
          schemaType = "MusicGroup"
        } else if (
          actor.actor_type === "orchestra" ||
          actor.actor_type === "ensemble"
        ) {
          schemaType = "MusicGroup"
        } else if (
          actor.actor_type === "group" ||
          actor.actor_type === "organization" ||
          actor.actor_type === "troupe" ||
          actor.actor_type === "touring_show_production"
        ) {
          schemaType = "PerformingGroup"
        }
        return compactBlank({
          "@type": schemaType,
          name: actor.name,
          url: actor.canonical_url,
          image: actor.image_url,
        })
      })

      // build venue/location with full address details if available
      const location = place.venue
        ? compactBlank({
            "@type": "Place",
            name: place.venue.name,
            url: place.venue.canonical_url,
            address: place.venue.city
              ? compactBlank({
                  "@type": "PostalAddress",
                  addressLocality: place.venue.city,
                  addressRegion: place.venue.region,
                  addressCountry: place.venue.country_code,
                })
              : undefined,
            geo:
              place.venue.latitude && place.venue.longitude
                ? compactBlank({
                    "@type": "GeoCoordinates",
                    latitude: place.venue.latitude,
                    longitude: place.venue.longitude,
                  })
                : undefined,
          })
        : place.venue_name
          ? { "@type": "Place", name: place.venue_name }
          : undefined

      // determine more specific event type based on categories
      // EventCategory = "arts" | "event" | "lifestyle" | "music" | "sports"
      let eventType = "Event"
      const firstCategory = place.categories?.[0]
      if (firstCategory === "music") {
        eventType = "MusicEvent"
      } else if (firstCategory === "sports") {
        eventType = "SportsEvent"
      } else if (firstCategory === "arts") {
        eventType = "TheaterEvent"
      }

      return buildPlaceSchema(eventType, place, {
        // only include eventStatus for future events (EventScheduled)
        // omit for past events as there is no "completed" status in schema.org
        eventStatus: !eventInPast
          ? "https://schema.org/EventScheduled"
          : undefined,
        startDate: hasSchedule
          ? formatYearMonthDayHourMinSecTz(
              schedule.start_at,
              schedule.time_zone
            )
          : undefined,
        endDate:
          hasSchedule && schedule.end_at
            ? formatYearMonthDayHourMinSecTz(
                schedule.end_at,
                schedule.time_zone
              )
            : undefined,
        location,
        performer: performers && performers.length > 0 ? performers : undefined,
        offers: schedule?.booking_url
          ? compactBlank({
              "@type": "Offer",
              url: schedule.booking_url,
              availability: isEventSoldOut(place)
                ? "https://schema.org/SoldOut"
                : "https://schema.org/InStock",
              priceCurrency: "USD",
              price: place.price_range,
              validFrom: hasSchedule
                ? formatYearMonthDayHourMinSecTz(
                    schedule.start_at,
                    schedule.time_zone
                  )
                : undefined,
            })
          : place.price_range
            ? compactBlank({
                "@type": "Offer",
                priceCurrency: "USD",
                price: place.price_range,
              })
            : undefined,
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        isAccessibleForFree: place.price_range === "free",
        // indicate if this is part of a recurring event series
        superEvent: place.is_recurring
          ? {
              "@type": "EventSeries",
              name: place.name,
            }
          : undefined,
      })
    }
    default:
      return null
  }
}

// FAQPage schema from a place's pre-generated Q&A
export function generatePlaceFaqSchema(place: Place) {
  if (!("detailed" in place) || !place.detailed) return null

  const answered = (place.sample_questions_v2 ?? []).filter(isAnsweredQuestion)
  if (answered.length === 0) return null

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: answered.map((q) => ({
      "@type": "Question",
      name: q.text,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer_summary?.trim() ?? "",
      },
    })),
  }
}

export function jsonLdScript(schema: object): string {
  return JSON.stringify(schema).replace(/</g, "\\u003c")
}

export function generateProfilePageSchema(
  user: CurrentUser | PublicUser,
  userProfile: CurrentUserProfile | PublicUserProfile,
  options?: {
    likesCount?: number
    postsCount?: number
  },
  isSearchableUser?: boolean
) {
  if (!user || !userProfile) return undefined

  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: generatePersonSchema(user, userProfile, {
      ...options,
      isSearchableUser,
    }),
  }
}

interface SchemaOrgWebPage {
  "@type": "WebPage"
  "@id": string
  url: string
  name: string
  isPartOf: {
    "@type": "WebSite"
    name: string
    url: string
  }
  breadcrumb: {
    "@type": "BreadcrumbList"
    itemListElement: Array<{
      "@type": "ListItem"
      position: number
      item: {
        "@id": string
        name: string
      }
    }>
  }
  primaryImageOfPage?: {
    "@type": "ImageObject"
    url: string
  }
  mainEntity: { "@id": "#article" }
}

interface SchemaOrgBlogPosting {
  "@type": "BlogPosting"
  "@id": "#article"
  headline: string
  description: string
  about?: {
    "@type": "TouristDestination"
    name: string
  }
  dateModified: string
  author: ReturnType<typeof generatePersonSchema>
  publisher: {
    "@type": "Organization"
    name: "Mindtrip"
    url: string
  }
}

interface SchemaOrgTouristDestination {
  "@type": "TouristDestination"
  "@id": string
  name: string
}

interface SchemaOrgTouristAttraction {
  "@type": "TouristAttraction"
  "@id": string
  name: string
  description?: string
  url?: string
}

type SchemaOrgEntity =
  | SchemaOrgWebPage
  | SchemaOrgBlogPosting
  | SchemaOrgTouristDestination
  | SchemaOrgTouristAttraction

interface SchemaOrgGraph {
  "@context": "https://schema.org"
  "@graph": SchemaOrgEntity[]
}

export function generateInspirationLdJson(
  guide: DetailedInspiration
): SchemaOrgGraph {
  const canonicalUrl = guide.canonical_url
  const heroImage = guide.photos?.[0]?.url

  const webPage: SchemaOrgWebPage = {
    "@type": "WebPage",
    "@id": canonicalUrl,
    url: canonicalUrl,
    name: guide.title,
    isPartOf: {
      "@type": "WebSite",
      name: "Mindtrip",
      url: BASE_CANONICAL_URL,
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          item: {
            "@id": `${BASE_CANONICAL_URL}/inspiration`,
            name: "Inspiration",
          },
        },
        {
          "@type": "ListItem",
          position: 2,
          item: {
            "@id": canonicalUrl,
            name: guide.title,
          },
        },
      ],
    },
    mainEntity: { "@id": "#article" },
  }

  if (heroImage) {
    webPage.primaryImageOfPage = {
      "@type": "ImageObject",
      url: heroImage,
    }
  }

  const blogPosting: SchemaOrgBlogPosting = {
    "@type": "BlogPosting",
    "@id": "#article",
    headline: guide.title,
    description: guide.content
      ? getFirstTwoSentences(guide.content)
      : guide.title,
    dateModified: formatYearMonthDayHourMinSecTz(
      guide.last_edited_at,
      guide.time_zone
    ),
    author: generatePersonSchema(guide.owner),
    publisher: {
      "@type": "Organization",
      name: "Mindtrip",
      url: BASE_CANONICAL_URL,
    },
  }

  if (guide.location) {
    blogPosting.about = {
      "@type": "TouristDestination",
      name: guide.location.name,
    }
  }

  const entities: SchemaOrgEntity[] = [webPage, blogPosting]

  if (guide.location) {
    entities.push({
      "@type": "TouristDestination",
      "@id": `#${guide.location.name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")}`,
      name: guide.location.name,
    })
  }

  guide.items
    ?.filter((item) => item.detailed && item.place)
    .forEach((item, index) => {
      if (item.detailed && item.place) {
        entities.push({
          "@type": "TouristAttraction",
          "@id": `#attraction-${index}`,
          name: item.place.name,
          description: item.content || undefined,
          url: item.place.canonical_url,
        })
      }
    })

  return {
    "@context": "https://schema.org",
    "@graph": entities,
  }
}
